import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cloud, X, ArrowUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const REMINDER_DISMISSED_KEY = "wwi_signin_reminder_dismissed";
const REMINDER_DISMISSED_AT_KEY = "wwi_signin_reminder_dismissed_at";
const ENGAGEMENT_KEY = "wwi_engagement";

// Check if we should show the reminder
const shouldShowReminder = () => {
  // Check if permanently dismissed
  const dismissed = localStorage.getItem(REMINDER_DISMISSED_KEY);
  if (dismissed === 'permanent') return false;
  
  // Check if dismissed recently (resurface at 10 tasks)
  const dismissedAt = localStorage.getItem(REMINDER_DISMISSED_AT_KEY);
  const stored = localStorage.getItem(ENGAGEMENT_KEY);
  if (!stored) return false;
  
  const engagement = JSON.parse(stored);
  const taskCount = engagement.taskCount || 0;
  
  // If dismissed before, only show again at 10+ tasks
  if (dismissedAt) {
    const dismissedAtCount = parseInt(dismissedAt, 10);
    // Show again if they've added 7+ more tasks since dismissing
    if (taskCount < dismissedAtCount + 7) return false;
  }
  
  // Show if 3+ tasks
  return taskCount >= 3;
};

export const SignInReminder = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const { isAuthenticated, loading } = useAuth();

  useEffect(() => {
    // Don't show while auth is loading or if already signed in
    if (loading || isAuthenticated) {
      setIsVisible(false);
      return;
    }

    // Delay showing to not interrupt immediate flow
    const timer = setTimeout(() => {
      if (shouldShowReminder()) {
        setIsVisible(true);
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [isAuthenticated, loading]);

  // Re-check when tasks are added (poll localStorage)
  useEffect(() => {
    if (isAuthenticated || loading) return;
    
    const checkInterval = setInterval(() => {
      if (!isVisible && shouldShowReminder()) {
        setIsVisible(true);
      }
    }, 5000);

    return () => clearInterval(checkInterval);
  }, [isVisible, isAuthenticated, loading]);

  const handleDismiss = (permanent = false) => {
    setIsVisible(false);
    
    if (permanent) {
      localStorage.setItem(REMINDER_DISMISSED_KEY, 'permanent');
    } else {
      // Store the task count when dismissed so we can resurface later
      const stored = localStorage.getItem(ENGAGEMENT_KEY);
      if (stored) {
        const engagement = JSON.parse(stored);
        localStorage.setItem(REMINDER_DISMISSED_AT_KEY, String(engagement.taskCount || 0));
      }
      localStorage.setItem(REMINDER_DISMISSED_KEY, 'temp');
    }
  };

  if (!isVisible || isAuthenticated || loading) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="fixed top-16 right-4 z-50 max-w-[260px]"
        data-testid="signin-reminder"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Arrow pointing up towards Sign In button */}
        <motion.div 
          className="absolute -top-4 right-[50%] text-[#E8EDEB]"
          animate={{ y: [0, -3, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowUp size={17} className="text-[#3DC4D0]" />
        </motion.div>
        
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-md border border-stone-100/80 px-4 py-3 relative">
          {/* Close button - subtle */}
          <button
            onClick={() => handleDismiss(false)}
            className="absolute top-2 right-2 p-1 text-stone-300 hover:text-stone-500 transition-colors"
            aria-label="Dismiss"
            data-testid="signin-reminder-dismiss"
          >
            <X size={14} />
          </button>

          {/* Content - compact */}
          <div className="flex items-start gap-3 pr-4">
            <div className="p-1.5 bg-[#E0F7FA]/60 rounded-lg flex-shrink-0 mt-0.5">
              <Cloud size={14} className="text-[#3DC4D0]" />
            </div>
            
            <div className="flex-1 min-w-0">
              <p className="text-xs text-[#52525B] leading-relaxed">
                <span className="font-medium text-[#27272A]">Your tasks live here.</span>
                {" "}Sign in to keep them safe across sessions.
              </p>
              
              {/* "Don't show again" - only visible on hover/focus */}
              <AnimatePresence>
                {isHovering && (
                  <motion.button
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    onClick={() => handleDismiss(true)}
                    className="text-[10px] text-stone-400 hover:text-stone-600 mt-2 transition-colors"
                    data-testid="signin-reminder-permanent-dismiss"
                  >
                    Don't remind me again
                  </motion.button>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SignInReminder;
